import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Printer, RefreshCw, AlertTriangle, ListChecks } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { downloadAttendancePDF } from "../../lib/pdf";
import { AttendanceMobile } from "./AttendanceMobile";
import { BulkMarkDialog } from "./BulkMarkDialog";
import { CorrectionsPanel, RequestCorrectionDialog } from "./Corrections";

export const localIso = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const thisMonth = () => localIso().slice(0, 7);
export const errMsg = (e, fb) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : fb);

export const MARK_STYLE = {
  P: "bg-emerald-100 text-emerald-800", A: "bg-red-100 text-red-700", WO: "bg-slate-100 text-slate-500",
  L: "bg-sky-100 text-sky-800", H: "bg-amber-100 text-amber-800", HD: "bg-violet-100 text-violet-800",
};
const OPTIONS = [["P", "Present"], ["A", "Absent"], ["HD", "Half day"], ["L", "Leave"], ["WO", "Weekly off"], ["H", "Holiday"], ["", "Automatic (Jibble)"]];

function Legend() {
  const items = [["P", "Present"], ["A", "Absent"], ["HD", "Half day"], ["L", "Leave"], ["WO", "Weekly off"], ["H", "Holiday"]];
  return (
    <div className="flex flex-wrap gap-2 text-[11px]" data-testid="attendance-legend">
      {items.map(([k, l]) => <span key={k} className={`px-2 py-0.5 rounded-full font-semibold ${MARK_STYLE[k]}`}>{k} · {l}</span>)}
    </div>
  );
}

function CellMenu({ cell, onPick, onClose }) {
  if (!cell) return null;
  return (
    <div className="fixed inset-0 z-50" onClick={onClose} data-testid="attendance-menu-backdrop">
      <div className="absolute bg-white rounded-xl shadow-2xl border border-slate-200 p-1.5 w-52" style={{ left: Math.max(8, Math.min(cell.x, window.innerWidth - 216)), top: Math.max(8, Math.min(cell.y, window.innerHeight - 310)) }} onClick={(e) => e.stopPropagation()} data-testid="attendance-menu">
        <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500 font-bold">{cell.name} · {new Date(`${cell.day}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}{cell.hours ? ` · ${cell.hours}h on Jibble` : ""}</div>
        {OPTIONS.map(([k, l]) => (
          <button key={k || "auto"} onClick={() => onPick(k)} className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-left hover:bg-slate-50 ${cell.current === k ? "font-bold" : ""}`} data-testid={`attendance-set-${k || "auto"}`}>
            <span className={`w-7 text-center text-[10px] font-bold rounded ${MARK_STYLE[k] || "bg-slate-50 text-slate-400"}`}>{k || "↺"}</span>{l}
          </button>
        ))}
      </div>
    </div>
  );
}

const recount = (marks) => {
  const t = { P: 0, A: 0, L: 0, WO: 0, H: 0, HD: 0 };
  Object.values(marks).forEach((s) => { if (s in t) t[s] += 1; });
  return { ...t, paid_days: t.P + t.L + t.WO + t.H + t.HD * 0.5 };
};

function Grid({ data, isHr, onCell }) {
  return (
    <table className="text-xs border-collapse min-w-full" data-testid="attendance-grid">
      <thead><tr className="bg-slate-900 text-white">
        <th className="sticky left-0 z-10 bg-slate-900 text-left px-3 py-2 min-w-[140px]">Employee</th>
        {data.days.map((d) => { const dt = new Date(`${d}T00:00:00`); return (
          <th key={d} className={`px-0.5 py-1 font-semibold w-7 ${dt.getDay() === 0 ? "text-amber-300" : ""}`}>{dt.getDate()}<div className="text-[9px] opacity-70">{"SMTWTFS"[dt.getDay()]}</div></th>); })}
        {["P", "HD", "A", "L", "WO", "H"].map((k) => <th key={k} className="px-2">{k}</th>)}
      </tr></thead>
      <tbody>
        {data.rows.map((r) => (
          <tr key={r.user_id} className="border-b border-slate-100" data-testid={`attendance-row-${r.user_id}`}>
            <td className="sticky left-0 z-10 bg-white px-3 py-1.5 font-semibold text-slate-900 whitespace-nowrap">{r.name}<div className="text-[10px] font-normal text-slate-400">{r.employee_id}{!r.jibble_matched && data.jibble.ok ? " · not on Jibble" : ""}</div></td>
            {data.days.map((d) => (
              <td key={d} className="p-0.5 text-center">
                <button type="button" onClick={(e) => onCell(e, r, d)} title={r.hours?.[d] ? `${r.hours[d]} h on Jibble` : undefined}
                  className={`relative w-6 h-6 rounded text-[10px] font-bold transition-transform hover:scale-110 ${MARK_STYLE[r.marks[d]] || "text-slate-300"}`}
                  data-testid={`attendance-cell-${r.user_id}-${d}`}>{r.marks[d] || "·"}
                  {r.overridden?.includes(d) && <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-slate-900" />}</button>
              </td>
            ))}
            {[r.totals.P, r.totals.HD, r.totals.A, r.totals.L, r.totals.WO, r.totals.H].map((v, i) => <td key={i} className="px-2 text-center font-bold tabular-nums">{v}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function AttendanceTab({ isHr }) {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cell, setCell] = useState(null);
  const [bulk, setBulk] = useState(false);
  const [req, setReq] = useState(null);
  const [rk, setRk] = useState(0);
  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await api.get("/hr/attendance", { params: { month } })).data); }
    catch (e) { toast.error(errMsg(e, "Could not load attendance")); }
    finally { setLoading(false); }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const onCell = (e, row, day) => {
    if (new Date(`${day}T00:00:00`) > new Date()) return;
    if (!isHr) return setReq({ day, current: row.marks[day] || "" });
    const b = e.currentTarget.getBoundingClientRect();
    setCell({ x: b.left, y: b.bottom + 4, row, day, name: row.name, current: row.marks[day] || "", hours: row.hours?.[day] });
  };
  const pick = async (next) => {
    const { row, day } = cell;
    setCell(null);
    try {
      await api.put("/hr/attendance", { user_id: row.user_id, date: day, status: next || null });
      if (!next) return load();
      setData((d) => ({ ...d, rows: d.rows.map((r) => {
        if (r.user_id !== row.user_id) return r;
        const marks = { ...r.marks, [day]: next };
        return { ...r, marks, overridden: [...new Set([...(r.overridden || []), day])], totals: recount(marks) };
      }) }));
      toast.success(`Marked ${next}`);
    } catch (e) { toast.error(errMsg(e, "Could not update")); }
  };
  const refresh = async () => { await api.post("/hr/attendance/refresh").catch(() => {}); load(); };
  const unmatched = (data?.rows || []).filter((r) => !r.jibble_matched);

  return (
    <div className="space-y-4" data-testid="attendance-tab">
      <div className="grid grid-cols-2 md:flex md:flex-wrap items-end gap-2 md:gap-3">
        <div className="col-span-2 md:col-span-1"><div className="text-xs text-slate-500 mb-1 hidden md:block">Month</div>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10 w-full md:w-44" data-testid="attendance-month" /></div>
        {isHr && <Button variant="outline" onClick={() => setBulk(true)} disabled={!data} className="h-10" data-testid="attendance-bulk"><ListChecks className="h-4 w-4 mr-1.5" />Bulk mark</Button>}
        {isHr && <Button variant="outline" onClick={refresh} disabled={loading} className="h-10" data-testid="attendance-refresh"><RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />Sync Jibble</Button>}
        <Button onClick={() => data && downloadAttendancePDF(data)} disabled={!data} className="h-10 bg-slate-900 hover:bg-slate-800 text-white col-span-2 md:col-span-1" data-testid="attendance-print"><Printer className="h-4 w-4 mr-1.5" />Print<span className="hidden sm:inline">&nbsp;register</span></Button>
        <div className="hidden md:block md:ml-auto"><Legend /></div>
      </div>
      {data && !data.jibble.ok && (
        <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-3 flex gap-2" data-testid="attendance-jibble-error"><AlertTriangle className="h-4 w-4 shrink-0" />Jibble could not be reached, so sign-ins are not counted. Weekly offs, leave and manual marks still show. ({data.jibble.error})</div>
      )}
      {data?.jibble.ok && isHr && unmatched.length > 0 && (
        <details className="text-xs bg-slate-50 border border-slate-200 text-slate-600 rounded-lg px-3 py-2" data-testid="attendance-unmatched">
          <summary className="cursor-pointer font-semibold">{unmatched.length} not found on Jibble</summary>
          <div className="mt-1.5"><b>{unmatched.map((r) => r.name).join(", ")}</b>. Set their Jibble name under Edit, or tick "Not in payroll & attendance" for people like the proprietor.</div>
        </details>
      )}
      {isHr && <div className="hidden md:block text-[11px] text-slate-500">Tap any day to mark it, or use Bulk mark for many people and days at once. Your mark overrides Jibble (shown with a dot). Holidays and weekly offs fill in automatically. Under 4 hours on Jibble counts as a half day.</div>}
      <CellMenu cell={cell} onPick={pick} onClose={() => setCell(null)} />
      {isHr && <CorrectionsPanel isHr refreshKey={rk} onDecided={load} />}
      {!isHr && <div className="text-[11px] text-slate-500">Something wrong? Tap the day to ask HR for a correction.</div>}
      <RequestCorrectionDialog req={req} onClose={() => setReq(null)} onSent={() => setRk((k) => k + 1)} />
      {isHr && <BulkMarkDialog open={bulk} data={data} onClose={() => setBulk(false)} onDone={load} />}
      {data && <div className="md:hidden"><AttendanceMobile data={data} isHr={isHr} onCell={onCell} /></div>}
      <div className="hidden md:block bg-white border border-slate-200 rounded-xl overflow-x-auto">
        {!data ? <div className="p-10 text-center text-slate-500">Loading…</div> : <Grid data={data} isHr={isHr} onCell={onCell} />}
      </div>
      {!isHr && <CorrectionsPanel isHr={false} refreshKey={rk} />}
    </div>
  );
}
